import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Flame, Radar, Sparkles, UserPlus } from "lucide-react";
import { api, type Signal, type SignalWindow } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge, CategoryBadge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { VariationBadge } from "@/components/PriceTag";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSeo } from "@/lib/useSeo";
import { fromNowFR, formatNumber, cn } from "@/lib/utils";

const WINDOWS: { value: SignalWindow; label: string; hint: string }[] = [
  { value: "1h", label: "1 h", hint: "dernière heure" },
  { value: "4h", label: "4 h", hint: "4 dernières heures" },
  { value: "24h", label: "24 h", hint: "dernières 24 heures" },
];

// ─────────────────────── burst badge ───────────────────────
//
// Color-codes the burst ratio (recent activity vs the forum's own 7-day
// baseline). ×8+ is "on fire", ×3+ is heating up, below that it's just busy.
function BurstBadge({ ratio }: { ratio: number | null }) {
  if (ratio == null) {
    return (
      <Badge variant="outline" className="text-foreground-muted" title="Pas d'historique 7 j — forum nouveau ou silencieux jusqu'ici">
        nouveau
      </Badge>
    );
  }
  const label = `×${ratio >= 10 ? Math.round(ratio) : ratio.toFixed(1)}`;
  const cls =
    ratio >= 8
      ? "bg-destructive/15 text-destructive ring-1 ring-destructive/30"
      : ratio >= 3
        ? "bg-warning/15 text-warning ring-1 ring-warning/30"
        : "bg-info/15 text-info ring-1 ring-info/30";
  return (
    <Badge className={cn("tabular-nums font-semibold", cls)}>
      {ratio >= 8 && <Flame className="size-3 mr-0.5" />}
      {label}
    </Badge>
  );
}

// ─────────────────────── page ───────────────────────

export default function SignalsPage() {
  useSeo({
    title: "Signaux d'activité — forums qui s'emballent",
    description:
      "Radar des forums Boursorama dont l'activité explose par rapport à leur rythme habituel, croisée avec le cours : repérez les emballements avant tout le monde.",
    canonicalPath: "/signals",
  });
  const [windowSel, setWindowSel] = useState<SignalWindow>("4h");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["signals", windowSel],
    queryFn: () => api.signals(windowSel, 30),
    refetchInterval: 60_000, // live radar — refresh every minute
  });
  const items = data?.items ?? [];
  const windowHint = WINDOWS.find((w) => w.value === windowSel)?.hint ?? windowSel;

  return (
    <div className="flex flex-col gap-6">
      {/* ── header ── */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-foreground flex items-center gap-2">
            <Radar className="size-6 text-primary" />
            Signaux
          </h1>
          <p className="text-sm text-foreground-muted">
            Forums dont l'activité <strong className="text-foreground">explose</strong> par
            rapport à leur rythme habituel ({windowHint} vs moyenne 7 jours), croisée avec
            le cours. Mise à jour automatique chaque minute.
          </p>
        </div>
        <div className="flex gap-1 self-start sm:self-auto" role="group" aria-label="Fenêtre d'analyse">
          {WINDOWS.map((w) => (
            <Button
              key={w.value}
              size="sm"
              variant={windowSel === w.value ? "default" : "outline"}
              onClick={() => setWindowSel(w.value)}
            >
              {w.label}
            </Button>
          ))}
        </div>
      </header>

      {/* ── content ── */}
      {isLoading ? (
        <div className="flex flex-col gap-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm flex flex-col items-center gap-3">
            <span className="text-foreground-muted">
              Impossible de charger les signaux (erreur réseau).
            </span>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Réessayer
            </Button>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center flex flex-col items-center gap-3">
            <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Sparkles className="size-6" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Marché calme</h2>
            <p className="text-sm text-foreground-muted max-w-md">
              Aucun forum ne sort de son rythme habituel sur cette fenêtre.
              Essayez une fenêtre plus large, ou revenez quand Paris ouvre.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile : cards */}
          <div className="md:hidden flex flex-col gap-2">
            {items.map((s) => (
              <SignalCard key={s.forum_id} signal={s} />
            ))}
          </div>

          {/* Desktop : table */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Burst</TableHead>
                  <TableHead className="min-w-[12rem]">Forum</TableHead>
                  <TableHead className="text-right">Messages ({windowSel})</TableHead>
                  <TableHead className="text-right">Auteurs</TableHead>
                  <TableHead className="text-right">Cours</TableHead>
                  <TableHead className="text-right">Var.</TableHead>
                  <TableHead className="text-right">Dernier message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.forum_id}>
                    <TableCell>
                      <BurstBadge ratio={s.burst_ratio} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        <Link
                          to={`/forums/${s.forum_id}/threads`}
                          className="font-medium hover:text-primary truncate"
                        >
                          {s.title || s.slug}
                        </Link>
                        <CategoryBadge category={s.category} />
                      </div>
                      <div className="text-xs text-foreground-muted">
                        habituellement ~{formatNumber(Math.round(s.baseline))} msg / {windowSel}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-foreground">
                      {formatNumber(s.posts_recent)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span>{formatNumber(s.authors_recent)}</span>
                      {s.new_authors > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="ml-1.5 inline-flex items-center gap-0.5 text-warning text-xs cursor-help">
                              <UserPlus className="size-3" />
                              {s.new_authors}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {s.new_authors} compte{s.new_authors > 1 ? "s" : ""} créé
                            {s.new_authors > 1 ? "s" : ""} il y a moins de 7 jours — possible
                            campagne coordonnée.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.last_price != null
                        ? `${formatNumber(s.last_price)} ${s.price_currency ?? ""}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <VariationBadge value={s.last_variation_pct} />
                    </TableCell>
                    <TableCell className="text-right text-xs text-foreground-muted">
                      {s.last_post_at ? fromNowFR(s.last_post_at) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

// ─────────────────────── mobile card ───────────────────────

function SignalCard({ signal: s }: { signal: Signal }) {
  return (
    <Card>
      <CardContent className="p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <Link
            to={`/forums/${s.forum_id}/threads`}
            className="font-medium hover:text-primary truncate"
          >
            {s.title || s.slug}
          </Link>
          <BurstBadge ratio={s.burst_ratio} />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-muted">
          <span>
            <strong className="text-foreground">{formatNumber(s.posts_recent)}</strong> messages
          </span>
          <span>{formatNumber(s.authors_recent)} auteurs</span>
          {s.new_authors > 0 && (
            <span className="inline-flex items-center gap-0.5 text-warning">
              <UserPlus className="size-3" />
              {s.new_authors} nouveaux
            </span>
          )}
          {s.last_price != null && (
            <span className="inline-flex items-center gap-1">
              {formatNumber(s.last_price)} {s.price_currency ?? ""}
              <VariationBadge value={s.last_variation_pct} />
            </span>
          )}
          {s.last_post_at && <span>dernier {fromNowFR(s.last_post_at)}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
